// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/pledge.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / header-actions).

import type { ListPageConfig } from "../../components/composition/ListPage";
import { pledgesApi } from "../../api/pledges";
import type { Pledge } from "../../api/types";
// override-block:imports BEGIN
import type { PledgeStatus, PledgeFrequency } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1330 — bulk-tag UX (closes DRIFT-648).
import { openBulkTagDialog } from "../../lib/bulk-tag-dialog";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<PledgeStatus, string> = {
  committed: "Committed",
  in_progress: "In progress",
  fulfilled: "Fulfilled",
  lapsed: "Lapsed",
  canceled: "Canceled",
};
const FREQUENCY_LABEL: Record<PledgeFrequency, string> = {
  one_time: "One-time",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

function donorLabel(row: Pledge): string {
  if (row.donor_name) return row.donor_name;
  if (row.donor_email) return row.donor_email;
  return row.donor_id ? row.donor_id.slice(0, 8) + "…" : "—";
}
// override-block:helpers END

export const pledgeListConfig: ListPageConfig<Pledge> = {
  title: "Pledges",
  subtitle: "Multi-payment commitments — track fulfillment pace and surface pledges at risk of lapsing.",
  queryKey: ["pledges", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search, filters }) =>
    pledgesApi.listPledges({ page, pageSize, sortField, sortDirection, search, filters }),
  defaultSort: { field: "due_date", direction: "asc" },
  pageSize: 50,
  rowHref: "/pledges/{id}",
  search: { enabled: true, placeholder: "Search pledges by donor name, email, phone, campaign, or designation…" },
  ambientRow: {
    stageField: "stage",
    entity: "pledge",
    activeStages: ["at_risk", "falling_behind"],
  },
  emptyState: {
    title: "No pledges yet",
    subtitle: "Pledges appear here once a donor commits to a multi-payment gift via dashboard, kiosk, or widget.",
  },
  headerActions: [
    // override-block:header-actions BEGIN
    { id: "record-payment", label: "Record payment", variant: "primary", permissionGate: toGate(GATE_CATALOG.pledge_list["record-payment"], "hide") },
    { id: "send-reminder", label: "Send reminder", variant: "ghost", permissionGate: toGate(GATE_CATALOG.pledge_list["send-reminder"], "disable") },
    // override-block:header-actions END
  ],
  filters: [
    { field: "source", label: "Source", type: "select", options: [{ value: "dashboard", label: "Dashboard" }, { value: "kiosk", label: "Kiosk" }, { value: "widget", label: "Widget" }, { value: "public", label: "Public" }, { value: "session", label: "Session" }, { value: "import", label: "Import" }] },
    { field: "designation_id", label: "Designation", type: "select", dynamicOptionsSource: "designations" },
    { field: "campaign_id", label: "Campaign", type: "select", dynamicOptionsSource: "campaigns" },
  ],
  bulkActions: [
    {
      id: "tag",
      label: "Tag selected",
      variant: "primary",
      permission: "alora-giving.tag.attach",
      permissionFallback: "hide",
      onAction:
        // override-block:bulk-action-tag BEGIN
        (ids) => openBulkTagDialog({ entityType: "pledge", ids }),
        // override-block:bulk-action-tag END
    },
  ],
  filterChips: [
    { id: "overdue", label: "Overdue", field: "overdue", value: true },
  ],
  columns: [
    // override-block:column-donor-id BEGIN
    { field: "donor_id", label: "Donor", type: "custom", sortable: true, render: (row) => donorLabel(row) },
    // override-block:column-donor-id END
    { field: "total_amount_minor_units", label: "Total", type: "currency", currencyField: "currency", sortable: true },
    { field: "fulfilled_minor_units", label: "Fulfilled", type: "currency", currencyField: "currency", sortable: true },
    { field: "outstanding_minor_units", label: "Outstanding", type: "currency", currencyField: "currency" },
    { field: "due_date", label: "Due", type: "date", sortable: true },
    // override-block:column-frequency BEGIN
    { field: "frequency", label: "Frequency", type: "custom", render: (row) => FREQUENCY_LABEL[row.frequency] ?? row.frequency },
    // override-block:column-frequency END
    // override-block:column-status BEGIN
    { field: "status", label: "Status", type: "custom", render: (row) => STATUS_LABEL[row.status] ?? row.status },
    // override-block:column-status END
    { field: "stage", label: "Stage", type: "stage-chip", entity: "pledge" },
  ],
};
